import { useEffect, useRef, useState } from "react";
import { useReveal } from "@/hooks/useReveal";
import { FABRIC_PHOTOS } from "@/data/fabricPhotos";

// A quiet, auto-drifting horizontal reel of real fabric photos, sitting just
// above the footer. It slowly scrolls on its own (pausing on hover/drag/focus),
// can be dragged/swiped, and any photo opens in a zoom lightbox. Every image
// carries keyword alt text for image SEO. Motion is disabled under
// prefers-reduced-motion; dragging still works there.
export const FabricReel = () => {
  const [ref, shown] = useReveal({ threshold: 0.12 });
  const scrollerRef = useRef(null);
  const stateRef = useRef({ paused: false, dragging: false, startX: 0, startScroll: 0, moved: 0 });
  const [active, setActive] = useState(null);

  // Auto-drift via rAF, seamless because the list is rendered twice.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return undefined;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return undefined;
    let raf;
    const step = () => {
      const s = stateRef.current;
      if (!s.paused && !s.dragging && active === null) {
        el.scrollLeft += 0.5;
        const half = el.scrollWidth / 2;
        if (half > 0 && el.scrollLeft >= half) el.scrollLeft -= half;
      }
      raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active]);

  const onPointerDown = (e) => {
    const el = scrollerRef.current;
    const s = stateRef.current;
    s.dragging = true;
    s.moved = 0;
    s.startX = e.clientX;
    s.startScroll = el.scrollLeft;
    if (el.setPointerCapture) el.setPointerCapture(e.pointerId);
  };
  const onPointerMove = (e) => {
    const s = stateRef.current;
    if (!s.dragging) return;
    const dx = e.clientX - s.startX;
    s.moved = Math.max(s.moved, Math.abs(dx));
    scrollerRef.current.scrollLeft = s.startScroll - dx;
  };
  const onPointerUp = (e) => {
    const s = stateRef.current;
    s.dragging = false;
    const el = scrollerRef.current;
    if (el && el.releasePointerCapture && e.pointerId != null) {
      try { el.releasePointerCapture(e.pointerId); } catch (err) { /* ignore */ }
    }
  };

  const openAt = (i) => {
    if (stateRef.current.moved < 6) setActive(i);
  };

  // Lightbox: Escape closes, arrows step, body scroll locked while open.
  useEffect(() => {
    if (active === null) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") setActive(null);
      else if (e.key === "ArrowRight") setActive((v) => (v + 1) % FABRIC_PHOTOS.length);
      else if (e.key === "ArrowLeft") setActive((v) => (v - 1 + FABRIC_PHOTOS.length) % FABRIC_PHOTOS.length);
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [active]);

  const loop = FABRIC_PHOTOS.concat(FABRIC_PHOTOS);

  return (
    <section id="fabric-gallery" data-testid="section-fabric-gallery" className="seam-top bg-ink">
      <style>{".fabric-reel::-webkit-scrollbar{display:none}"}</style>
      <div
        ref={ref}
        className={`mx-auto max-w-[1400px] px-5 pt-[72px] md:px-7 transition-[opacity,transform] duration-[600ms] ease-out ${
          shown ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
        }`}
      >
        <p className="eyebrow text-xs text-kraft">Inside the warehouse</p>
        <h2 className="font-display mt-3 text-xl font-extrabold leading-[1.1] text-warmwhite md:text-2xl">
          Fabric on our floor.
        </h2>
        <p className="mt-2 max-w-xl font-inter text-sm leading-[1.6] text-warmwhite/55">
          Spandex, lycra, prints, and stretch fabric, by the roll or the pound. Drag to
          browse, tap any photo to zoom.
        </p>
      </div>

      <div
        ref={scrollerRef}
        className="fabric-reel mt-8 flex cursor-grab gap-3 overflow-x-auto px-5 pb-[80px] active:cursor-grabbing md:px-7"
        style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onMouseEnter={() => { stateRef.current.paused = true; }}
        onMouseLeave={() => { stateRef.current.paused = false; }}
      >
        {loop.map((ph, i) => (
          <button
            key={i}
            type="button"
            aria-label={`Zoom in: ${ph.alt}`}
            onFocus={() => { stateRef.current.paused = true; }}
            onBlur={() => { stateRef.current.paused = false; }}
            onClick={() => openAt(i % FABRIC_PHOTOS.length)}
            className="group relative h-[180px] w-[180px] shrink-0 overflow-hidden bg-charcoal focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-kraft md:h-[220px] md:w-[220px]"
          >
            <img
              src={ph.thumb}
              alt={ph.alt}
              width="220"
              height="220"
              loading="lazy"
              decoding="async"
              draggable="false"
              className="h-full w-full select-none object-cover transition-transform duration-500 ease-out group-hover:scale-105"
            />
          </button>
        ))}
      </div>

      {active !== null && (
        <div
          className="fixed inset-x-0 top-0 z-[60] flex h-dvh items-center justify-center bg-ink/95 p-4"
          data-testid="fabric-lightbox"
          onClick={() => setActive(null)}
        >
          <img
            src={FABRIC_PHOTOS[active].src}
            alt={FABRIC_PHOTOS[active].alt}
            className="max-h-[88vh] max-w-[92vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            aria-label="Close"
            onClick={() => setActive(null)}
            className="eyebrow absolute right-4 top-4 flex h-11 items-center border border-warmwhite/40 px-4 text-[12px] text-warmwhite transition-colors duration-300 ease-out hover:bg-warmwhite hover:text-ink"
          >
            Close
          </button>
        </div>
      )}
    </section>
  );
};
